/* ORCHID — Share the Table: the referral engine as scoped in The Ritual.
 *
 * Blueprint ch. 05/09/10, CHANGE-REQUEST-2026-09-01 §2. What The Ritual bought is the on-site
 * machinery: a link per customer, the discount codes, attribution of who referred whom, and the
 * Twin-Gift prompt as ONE TAP THEN A FORM — never "one click", because a Shopify post-purchase
 * step cannot take a second address, and that shape is written into the agreement. The printed
 * QR envelope and a referral app are the tier above; nothing here depends on either.
 *
 * No server of ours. Everything is Shopify's own routes plus this file:
 *
 *   /discount/FRIEND10?redirect=/pages/subscriptions?ref=<id>
 *       Shopify applies the friend's code and lands them on the site with ?ref= intact
 *       (verified: 302 → /pages/subscriptions?ref=…). This file keeps `ref` for 30 days and
 *       writes it onto the cart as the attribute "Referred by", so the friend's order carries
 *       the sender's customer id. That is the attribution. The Friday export reads it.
 *
 *   /?twin=<tier>            (NOT /discount/TWIN25 — that parks the code in the browser
 *                              session and discounts the visitor's own next order. The 25% is
 *                              applied on the twin checkout URL instead, see checkoutUrl.)
 *       The Twin-Gift prompt (confirmation email, and the Share section). Landing with ?twin=
 *       opens that tier's chooser, applies the 25% code, and shows the short recipient form:
 *       name, address, a note. The name and note travel as line-item properties; the address
 *       pre-fills the checkout's shipping fields through Shopify's own checkout[...] params.
 *
 * Filter rules, same as every other file here: nothing third-party, no images in the script,
 * localStorage only for the referral token (same-origin, and it degrades to "no attribution",
 * never to "no sale"). Init is idempotent for the theme editor.
 */
(function () {
  'use strict';
  if (window.orchidReferralLoaded) return;
  window.orchidReferralLoaded = true;

  var KEY = 'orchid_ref';
  var DAYS = 30;
  var ATTR = 'Referred by';

  function read(name) {
    var m = window.location.search.match(new RegExp('[?&]' + name + '=([^&#]*)'));
    // A fragment inside a /discount/ redirect arrives percent-encoded, as literal text in the
    // query (?twin=card_classic%23hosting-fit). Cut at the first '#' so it cannot poison a tier.
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')).split('#')[0] : '';
  }

  function safe(v) {
    // A referral token is a customer id or an order name. Nothing else gets stored.
    return /^[A-Za-z0-9_-]{1,40}$/.test(v) ? v : '';
  }

  function store(ref) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ ref: ref, until: Date.now() + DAYS * 864e5 }));
    } catch (e) { /* private mode: attribution is lost, the sale is not */ }
  }

  function stored() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return '';
      var v = JSON.parse(raw);
      if (!v || !v.ref || v.until < Date.now()) return '';
      return safe(v.ref);
    } catch (e) { return ''; }
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok ? r.json() : Promise.reject(r); });
  }

  /* --- Attribution: the referrer rides on the cart ------------------------ */

  var written = false;
  function attribute() {
    var ref = stored();
    if (!ref || written) return;
    written = true;
    // Only write when it is not already there: a cart update fires Prestige's cart events, and
    // there is no reason to fire them on every page view.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && cart.attributes && cart.attributes[ATTR] === ref) return;
        var attrs = {}; attrs[ATTR] = ref;
        return postJSON('/cart/update.js', { attributes: attrs });
      })
      .catch(function () { written = false; });
  }

  /* --- The twin: one tap opened it, this is the form ---------------------- */

  var twin = null;   // { tier, fields: {...} } while a twin is in progress

  function mountForm(dialog) {
    if (!dialog || dialog.querySelector('[data-orchid-twin-form]')) return;
    var actions = dialog.querySelector('.orchid-chooser__actions');
    if (!actions) return;

    var wrap = document.createElement('div');
    wrap.className = 'orchid-twin';
    wrap.setAttribute('data-orchid-twin-form', '');
    wrap.innerHTML =
      '<p class="orchid-label orchid-label--sm orchid-twin__eyebrow">Share the table &middot; 25% off</p>' +
      '<p class="orchid-twin__lede">The same table, sent to someone else. Tell us who, and the studio does the rest.</p>' +
      '<div class="orchid-twin__grid">' +
        field('first_name', 'Their first name', 'given-name') +
        field('last_name', 'Their last name', 'family-name') +
        field('address1', 'Their address', 'address-line1', 'orchid-twin__wide') +
        field('address2', 'Apartment, floor (optional)', 'address-line2', 'orchid-twin__wide') +
        field('zip', 'ZIP', 'postal-code') +
        field('phone', 'Their phone, for the driver', 'tel') +
      '</div>' +
      '<label class="orchid-twin__note"><span class="orchid-label orchid-label--sm">A note for the card (optional)</span>' +
        '<textarea name="note" maxlength="240" rows="2" data-orchid-twin-field="note"></textarea></label>' +
      '<p class="orchid-twin__help">Brooklyn only, delivered Friday morning. You pay now; they receive it with your note and no price.</p>' +
      '<p class="orchid-twin__error" role="alert" hidden></p>';
    actions.parentNode.insertBefore(wrap, actions);
  }

  function field(name, label, autocomplete, cls) {
    return '<label class="orchid-twin__field ' + (cls || '') + '">' +
      '<span class="orchid-label orchid-label--sm">' + label + '</span>' +
      '<input type="text" name="' + name + '" autocomplete="' + autocomplete + '" data-orchid-twin-field="' + name + '"' +
      (name === 'zip' ? ' inputmode="numeric" maxlength="5"' : '') + '></label>';
  }

  function fields(dialog) {
    var out = {};
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-orchid-twin-field]'), function (el) {
      out[el.getAttribute('data-orchid-twin-field')] = (el.value || '').trim();
    });
    return out;
  }

  function validate(dialog) {
    var f = fields(dialog);
    var err = dialog.querySelector('.orchid-twin__error');
    var missing = ['first_name', 'last_name', 'address1', 'zip'].filter(function (k) { return !f[k]; });
    var msg = '';
    if (missing.length) msg = 'We need their name, address and ZIP to send it.';
    else if (!/^\d{5}$/.test(f.zip)) msg = 'A Brooklyn ZIP is five digits.';
    else if (typeof window.orchidCheckZip === 'function' && window.orchidCheckZip(f.zip).status !== 'eligible') {
      msg = 'We deliver across Brooklyn only for now, so we cannot send to that ZIP.';
    }
    if (err) { err.textContent = msg; err.hidden = !msg; }
    return msg ? null : f;
  }

  /* --- What the chooser asks this file ------------------------------------ */

  window.orchidTwin = {
    // Line-item properties for the twin line. Null when no twin is in progress, or the form
    // does not validate — in which case the chooser must not add anything.
    properties: function (dialog) {
      if (!twin) return {};
      var f = validate(dialog);
      if (!f) return null;
      twin.fields = f;
      var p = { 'Twin gift for': (f.first_name + ' ' + f.last_name).trim() };
      if (f.note) p['Card note'] = f.note;
      var ref = stored();
      if (ref) p['Sent by'] = ref;
      return p;
    },
    // The checkout URL with the recipient's address already in the shipping fields.
    checkoutUrl: function (base) {
      if (!twin || !twin.fields) return base;
      var f = twin.fields;
      var q = {
        'checkout[shipping_address][first_name]': f.first_name,
        'checkout[shipping_address][last_name]': f.last_name,
        'checkout[shipping_address][address1]': f.address1,
        'checkout[shipping_address][address2]': f.address2,
        'checkout[shipping_address][city]': 'Brooklyn',
        'checkout[shipping_address][province]': 'NY',
        'checkout[shipping_address][zip]': f.zip,
        'checkout[shipping_address][country]': 'US',
        'checkout[shipping_address][phone]': f.phone
      };
      var parts = [];
      Object.keys(q).forEach(function (k) { if (q[k]) parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(q[k])); });
      /* The 25% belongs to THIS checkout, not to the browser.
         It used to be parked in the session by a /discount/TWIN25 link, which is
         how a discount code works: once applied it sits in the cart cookie and
         comes off whatever that browser buys next. Measured on the live store —
         a clean session paid $49.00, a session that had followed a twin link
         paid $36.75 on a plain subscription for itself. So somebody who clicked
         "send a twin", changed their mind and subscribed for themselves got 25%
         off a subscription nobody meant to discount.
         Applied here instead, it reaches the twin order and nothing else. */
      parts.push('discount=TWIN25');
      return base + (base.indexOf('?') > -1 ? '&' : '?') + parts.join('&');
    },
    active: function () { return !!twin; }
  };

  /* --- Landing ------------------------------------------------------------- */

  function init() {
    var ref = safe(read('ref'));
    if (ref) store(ref);
    attribute();

    var tier = safe(read('twin'));
    if (!tier) return;
    // The trigger for that tier, wherever it is on the page.
    var trigger = document.querySelector('[data-orchid-chooser][data-tier="' + tier + '"]');
    if (!trigger) return;
    twin = { tier: tier, fields: null };
    // Let the chooser mount, then open it and put the form in. The dialog is found the way
    // the chooser finds it: inside the trigger's own section, else the first on the page.
    window.setTimeout(function () {
      var section = trigger.closest('.shopify-section, section');
      var dialog = (section && section.querySelector('[data-orchid-chooser-dialog]')) ||
                   document.querySelector('[data-orchid-chooser-dialog]');
      mountForm(dialog);
      if (dialog) dialog.classList.add('is-twin');
      trigger.click();
      var focus = dialog && dialog.querySelector('[data-orchid-twin-field="first_name"]');
      if (focus) window.setTimeout(function () { focus.focus({ preventScroll: true }); }, 250);
    }, 60);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
